import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';

import diff from 'microdiff';

import { MotesLoginService, UserFactory, UserRights, FirebaseUserCredential } from '../motes-login/motes-login.service';

import { map, first, debounceTime, finalize, switchMap, tap, take, shareReplay, share, filter } from 'rxjs/operators';
import { combineLatest, of, Observable, merge, Subject, firstValueFrom } from 'rxjs';

import { User, MoteUnlockState } from '../models/user';
import { Media } from '../models/media';
import { Vote } from '../models/vote';
import { DatabaseService } from './database.service';

import { MotesPaths, ChallengeState, Difference, ChallengeStateMap, Voucher, DatabaseModel } from '../models';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { StatsService } from './stats.service';
import { LoginCredential } from '@motes-sdk/motes-login';

const usersPublicRef: string = MotesPaths.USER_PUBLIC_DB_PATH();
const usersPrivateRef: string = MotesPaths.USER_PRIVATE_DB_PATH();

// Path for admin requests
const adminsPublicRef: string = MotesPaths.ADMIN_REQ_PUBLIC_DB_PATH();
const adminsPrivateRef: string = MotesPaths.ADMIN_REQ_PRIVATE_DB_PATH();


// const MOTES_REST = "http://localhost:5000";
const FIREBASE_REST = "https://motesdev.firebaseio.com";

// const MOTES_REST = "https://motesdev.firebaseio.com";

@Injectable({ providedIn: 'root' })
export class UserService implements UserFactory {
    /** The current user, stored for convenience */
    public currentUser: User = null;
    // Shared user observable for current user
    private userObservable: Observable<User> = null;
    private watchAdminUsersReplay: Observable<User[]> = null;
    private watchAdminRequestReplay: Observable<User[]> = null;

    constructor(
        private af: DatabaseService,
        private http: HttpClient,
        private loginService: MotesLoginService,
        private datePipe: DatePipe,
        private statsService: StatsService,
    ) {
        if(typeof window === 'undefined') return;
        // Expose UserService for global usage (i.e. Cypress testing)
        if((window as any)?.Cypress) {
            (window as any).UserService = this;
        }
    }

    public init() {
        // this.initialized = true;
    }

    public async updateUser(user: User, removeMigrationData: boolean = false): Promise<any> {
        user.updatedOn = this.af.serverTime();
        const publicJson = user.toPublicJson();
        const privateJson = user.toPrivateJson();

        // Make sure not to overwrite migration data in existing user (in case of account linking)
        if(removeMigrationData) {
            delete publicJson['challengeStateMap'];
            delete privateJson['unlockedMotes'];
        }

        // Create update user content in two calls (in order to preserve "unlocked" etc. which is not part of update)
        let publicPart = this.af.object(usersPublicRef + user.key).update(publicJson);
        let privatePart = this.af.object(usersPrivateRef + user.key).update(privateJson);
        return Promise.all([publicPart, privatePart]);
    }

    public updateAdminRequest(user: User): Promise<any> {
        // Create update user content in two calls (in order to preserve "unlocked" etc. which is not part of update)
        let publicPart = this.af.object(adminsPublicRef + user.key).update(user.toPublicJson());
        let privatePart = this.af.object(adminsPrivateRef + user.key).update(user.toPrivateJson());
        return Promise.all([publicPart, privatePart]);
    }

    /**
    * Create a new user in user database and register a login
    * @param credentials Firebase credentials with provider data
    * @param username Name of the user
    */
    public createUser(credentials: LoginCredential, userRights: UserRights, username: string = null, removeMigrationData: boolean = false): Promise<any> {
        console.log('Creating new user, credentials: ', credentials, username)
        let user: User = new User(credentials.user.uid);

        user.email = credentials.user?.email || credentials.user?.providerData[0]?.email || '';
        user.userName = username || credentials.user.displayName || credentials.user.providerData[0]?.displayName || '';
        user.providerData = credentials.user.providerData[0] || null;
        user.createdOn = this.af.serverTime();
        user.updatedOn = this.af.serverTime();
        user.isAnonymous = userRights.isAnonymous;
        user.isAdmin = userRights.isAdmin;
        user.isSuperAdmin = userRights.isSuperAdmin;

        // Find profile picture in credentials
        const profileHasPictureUrl = typeof credentials.user.providerData[0]?.photoURL == 'string';
        const profilePictureUrl = credentials.user.providerData[0]?.photoURL;
        const profilePic = credentials.user.photoURL || profileHasPictureUrl ? profilePictureUrl : credentials.user.providerData[0]?.photoURL || "";
        user.profilePic.hd.url = profilePic;
        user.profilePic.original.url = profilePic;
        user.profilePic.thumb.url = profilePic;

        // Set unlockedMotes and challengeStateMap from migration user if applicable
        user.unlockedMotes = credentials.user["unlockedMotes"];
        user.challengeStateMap = credentials.user['challengeStateMap'];

        // Save user in db
        return this.updateUser(user, removeMigrationData);
    }

    /**
    * Create a new anonymous user (without profile pic and email) in user database and register a login
    * @param credentials Firebase credentials with provider data
    */
    public createAnonymousUser(credentials: LoginCredential, userRights: UserRights): Promise<any> {
        let user: User = new User(credentials.user.uid);
        user.userName = "anonymous";
        user.providerData = credentials.user.providerData[0] || null;
        user.createdOn = this.af.serverTime() || null;
        user.isAnonymousAccount = true;
        // These should always be false in this case
        user.isAnonymous = userRights.isAnonymous;
        user.isAdmin = userRights.isAdmin;
        user.isSuperAdmin = userRights.isSuperAdmin;

        return this.updateUser(user);
        // Obs: User login is created on auth - so dont do this.pushLogin(user.key);
    }

    public userExists(userKey: string): Promise<boolean> {
        // Get public user data
        return new Promise<boolean>(async (resolve) => {
            let publicPart = await this.af.object(usersPublicRef + userKey).valueChanges().pipe(first()).toPromise();
            let privatePart = await this.af.object(usersPrivateRef + userKey).valueChanges().pipe(first()).toPromise();
            resolve(publicPart != null && privatePart != null);
        });
    }

    public updateChallengePoints(userKey: string, challengeKey: string, state: ChallengeState): Promise<any> {
        if(!userKey) {
            console.error("No user key, can not save challenge state??", state);
            return Promise.resolve();
        }
        // console.log("Saving challenge ", MotesPaths.USER_CHALLENGE_STATE_DB_PATH(userKey, challengeKey));
        return this.af.object(MotesPaths.USER_CHALLENGE_STATE_DB_PATH(userKey, challengeKey)).update(state.toJson());
    }

    /**
     * Completely reset the challenge state of a user (only for debugging purposes)
     */
    public resetChallengeStateMap(userKey: string): Promise<any> {
        if(!userKey) {
            console.error("No user key, can not reset challenge state map??");
            return Promise.resolve();
        }
        return this.af.object(MotesPaths.USER_CHALLENGE_STATE_DB_PATH(userKey, "")).update({});
    }

    public pushLogin(userId: string, timestamp: number = null): void {
        this.af.list(usersPrivateRef + userId + '/logins').push(timestamp || this.af.serverTime())
    }

    public updateCreatedOn(userId: string): void {
        this.af.object(usersPrivateRef + userId + '/createdOn').set(this.af.serverTime());
    }

    public updatePredefinedProfilePic(userId: string, predefinedProfilePic: string) {
        this.af.object(MotesPaths.USER_PUBLIC_DB_PATH(userId) + '/predefinedProfilePic').set(predefinedProfilePic);
    }

    public deleteUser(userKey: string): Promise<void> {
        if(!userKey) return Promise.resolve();

        this.af.object(usersPrivateRef + "/" + userKey).remove();
        return this.af.object(usersPublicRef + "/" + userKey).remove();
    }

    public saveUnlockedMote(userKey: string, moteKey: string, tourKey: string, projectKey: string, saveUser: boolean = true): Promise<any> {
        let moteRef: MoteUnlockState = {
            moteKey: moteKey,
            tourKey: tourKey,
            userKey: userKey,
            projectKey: projectKey,
            createdOn: this.af.serverTime()
        }
        // Skip saving unlock in user in emulation / testing mode
        if(!saveUser && this.currentUser) {
            // Simulate saving unlock in user
            this.currentUser.unlockedMotes.push(moteRef);
            return;
        }

        const moteUpdate = this.af.object(usersPrivateRef + userKey + "/unlockedMotes/" + moteKey).update(moteRef);
        const updatedOn = this.af.object(usersPrivateRef + userKey).update({ "updatedOn" : this.af.serverTime() });
        return Promise.all([moteUpdate, updatedOn]);
    }

    public watchUsersByIds(ids: string[], getPrivate: boolean = false): Observable<User[]> {
        let userObs: Observable<User>[] = [];
        for(let id of ids) {
            userObs.push(this.watchUser(id, getPrivate));
        }
        return combineLatest(...userObs);
    }

    public removeUnlockedMote(user: User, moteKey: string): Promise<any> {
        return this.af.object(usersPrivateRef + user.key + "/unlockedMotes/" + moteKey).remove();
    }

    public watchUsername(uid: string): Observable<string> {
        return new Observable((observer) => {
            this.af.object(usersPublicRef + uid + '/userName').valueChanges().subscribe((json: any) => {
                observer.next(json);
            });
        });
    }

    public watchProfilePic(userKey: string): Observable<Media> {
        return new Observable((observer) => {
            this.af.object(usersPublicRef + userKey + '/profilePic').valueChanges().subscribe((json: any) => {
                let media: Media = new Media(json);
                observer.next(media);
            });
        });
    }

    public watchAllUsers(): Observable<Array<User>> {
        return new Observable((observer) => {
            let users: Array<User> = [];
            this.af.list('users/userNames').snapshotChanges().pipe(first(), map((usernames) => {
                usernames.map((username) => {
                    this.watchUser(username.key).pipe(first()).subscribe((user: User) => {
                        users.push(user);
                        observer.next(users);
                    });
                });
            })).subscribe();
        });
    }

    public watchAdminUsers(): Observable<any[]> {
        if(this.watchAdminUsersReplay) return this.watchAdminUsersReplay;

        let privateList = this.af.list(usersPrivateRef, query => query.orderByChild('isAdmin').equalTo(true));

        this.watchAdminUsersReplay = privateList.snapshotChanges().pipe(debounceTime(600), map((arr) => {
            // Merge private mote from DB and mapt to User object
            for(let user of arr) {
                this.af.object(usersPublicRef + user.key).snapshotChanges().pipe(first()).subscribe((privateSnap) => {
                    return User.fromPublicJson(privateSnap.key, privateSnap.payload.val());
                });
            }
            return arr.map((snap) => User.fromPrivateJson(snap.key, snap.payload.val()));
        }), shareReplay(1));
        return this.watchAdminUsersReplay;
    }


    /**
    * Create a new admin request
    */
    public createAdminRequest(user: User): Promise<any> {
        // Add admin request time to user
        user.adminRequestOn = Date.now();
        console.log("Admin request created on ", user.adminRequestOn);
        this.updateUser(user);
        // Create a new admin request for user
        let publicPart = this.af.object(adminsPublicRef + user.key).update(user.toPublicJson());
        let privatePart = this.af.object(adminsPrivateRef + user.key).update(user.toPrivateJson());

        let sendMail = this.sendAdminReqMail(user);
        let sendSlack = this.sendAdminReqSlack(user);

        return Promise.all([publicPart, privatePart]);
    }

    public watchAdminRequests(): Observable<any[]> {
        if(this.watchAdminRequestReplay) return this.watchAdminRequestReplay;
        // Merge private mote from DB and mapt to User object
        this.watchAdminRequestReplay = this.af.list(adminsPrivateRef, query => query.orderByChild('adminRequestOn')).snapshotChanges().pipe(debounceTime(500), map((arr) => {
            return arr.map((snap) => User.fromPrivateJson(snap.key, snap.payload.val()));
        }), shareReplay(1));
        return this.watchAdminRequestReplay;
    }

    public deleteAdminRequest(user: User, rejected: boolean): Promise<any> {
        if(!user.key) return Promise.resolve();

        if(rejected) {
            user.adminRequestOn = -1;
            this.updateUser(user);
        }

        let publicPart = this.af.object(adminsPrivateRef + "/" + user.key).remove();
        let privatePart = this.af.object(adminsPublicRef + "/" + user.key).remove();
        return Promise.all([publicPart, privatePart]);
    }

    public watchNormalUsers(): Observable<any[]> {
        let subject = new Subject<User[]>();
        let privateList = this.af.list(usersPrivateRef, query => query.orderByChild('isAdmin').startAt(null).endAt(false));
        //let publicList = this.af.list(usersPublicRef);

        // We cannot query all normal users as they dont all have a private entry
        // where the isAdmin is defined
        // TODO: Figure out why oh why firebase returns sooooo many times without debounch
        // isAnonymousAccount
        let userSub = privateList.snapshotChanges().pipe(debounceTime(50)).subscribe((arr) => {
            // Get all private data in the world and then merge private and public
            console.log("Got public users, now get private ", arr.length);
            this.af.list(usersPublicRef).snapshotChanges().pipe(first()).subscribe(snapArr => {
                console.log("Got private data ", snapArr.length);
                for(let userSnap of snapArr) {
                    User.fromPublicJson(userSnap.key, userSnap.payload.val());
                }
                // Map all users to model
                let users = arr.map((snap) => User.fromPrivateJson(snap.key, snap.payload.val()));
                subject.next(users);
            });
        });

        subject.pipe(finalize(() => {
            // Last subscriber unsubscribed
            if(userSub) userSub.unsubscribe();
        }));

        return subject;
    }

    private snapKeys(firebaseList) {
        return firebaseList.map(snap => snap.key);
    }

    public watchUsersWithEmail(max: number = 100000): Observable<User[]> {
        // Observable list with changes of users with a email address (private part)
        let privateObs = this.af.list(usersPrivateRef, query => query.orderByChild('email').startAt('a').endAt('\uf8ff').limitToFirst(max));
        // Switch map users in private list (public part)
        return privateObs.snapshotChanges().pipe(debounceTime(2000), tap(pri => console.log("Got private ", pri.length)), switchMap(privateList => this.watchUsers(this.snapKeys(privateList)),
            // Combine public and private part with switchMap selector function
            (privateList, publicList) => publicList.map((user: User) => {
                // Map private snap with public user object
                let privateSnap = privateList.find(snap => (snap.key == user.key));
                return user.updateFromJson({ private: privateSnap ? privateSnap.payload.val() : {} });
            })));
    }

    public watchAllUsersPlus(getPrivate: boolean = false): Observable<any[]> {
        // Get public user data
        // TODO Figure out why oh why Firebase returns 300 times with the same user array without debounce
        let publicObserver = this.af.list(usersPublicRef).snapshotChanges().pipe(debounceTime(500), map((arr) => {
            return arr.map((snap) => User.fromPublicJson(snap.key, snap.payload.val()));
        }));
        // Merge private mote from DB and mapt to User object
        if(!getPrivate) return publicObserver;

        let privateObserver = this.af.list(usersPrivateRef).snapshotChanges().pipe(debounceTime(500), map((arr) => {
            return arr.map((snap) => User.fromPrivateJson(snap.key, snap.payload.val()));
        }));

        // Merge private and public data (debaunch in order not to only one return call)
        return merge(publicObserver, privateObserver).pipe(debounceTime(600));
    }


    public watchUsers(userKeys: Array<string>, getPrivate: boolean = false): Observable<User[]> {
        // Create a observable for each user
        let userObservables = userKeys.map((key) => this.watchUser(key, getPrivate));

        // Merge array of user arrays and return it
        return combineLatest(...userObservables).pipe(debounceTime(500), map((users) => [].concat(...users)));
    }

    /**
    * Watch user login helper - watch user rights and request login if no
    * user is signed in or if only anonymous user is signed in
    * @return Returns a database user object
    */
    public watchCurrentUser(): Observable<User | null> {
        if(this.userObservable) return this.userObservable;

        // Subscribe for user right changes
        let watchUser = this.loginService.watchUserRights().pipe(share(), switchMap(userRights => {
            // Switch user subscription depending on current user rights
            if(!userRights || !userRights.uid) {
                // console.log("No userRights", userRights);
                this.currentUser = null;
                // If there is no user, dont subscribe for anything
                return of(null) as Observable<User | null>;
            }
            // Subscribe for database changes of current user
            return this.watchUser(userRights.uid, true).pipe(shareReplay(1), map(user => {
                // TODO: Fix watch user unsubscribe leak - console.log("Accumulates yes indeed more XiXiX", user)
                // OBS: Overwrite what ever info about user rights that is stored in db user
                // This is userfull in some rare cases where there is a mismatch between db and fb rights
                // ..and user rights are only stored in user object for convinience
                user.isAnonymous = userRights.isAnonymous;
                user.isAdmin = userRights.isAdmin;
                user.isSuperAdmin = userRights.isSuperAdmin;
                this.currentUser = user;
                return user;
            }));
        }));

        this.userObservable = watchUser.pipe(shareReplay(1));
        return this.userObservable;
    }

    /**
     * Watch diff of updates to the current user: When a user update is pushed from the database, the received data is compared to the locally stored user data.
     * @returns An observable of objects containing the current user and a `Difference[]`
     */
    public watchCurrentUserDiff(): Observable<{ user: User, diff: Difference[]; }> {
        let previousUserJson = null;

        return this.watchCurrentUser().pipe(
            // Filter out falsy values (e.g. `null` and `undefined`)
            filter<User>(Boolean),
            map((user) => {
                // Generate diff for both public and private data
                const publicDiff = diff(previousUserJson?.public || {}, user.toJson().public);
                const privateDiff = diff(previousUserJson?.private || {}, user.toJson().private);
                // Update `previousUserJson` for next invocation
                previousUserJson = user.toJson();

                return {
                    user: user,
                    diff: Object.assign(publicDiff, privateDiff)
                };
            }),
            // Filter out return values with no diff entries (i.e. where the object has not changed)
            filter((userDiffObj) => userDiffObj.diff.length > 0)
        );
    }

    public watchUser(userKey: string, getPrivate: boolean = false): Observable<User> {

        let userObserver: Observable<User>;

        // Get public user data
        userObserver = this.af.object(usersPublicRef + userKey).valueChanges().pipe(map((json) => {
            return User.fromPublicJson(userKey, json);
        }));

        // Merge private mote from DB and mapt to User object
        if(getPrivate) {
            let privateObserver = this.af.object(usersPrivateRef + userKey).valueChanges().pipe(map((json) => {
                return User.fromPrivateJson(userKey, json);
            }));

            // Add notification when private data is loaded in user object (debounch so only one notification is sent)
            userObserver = combineLatest(userObserver, privateObserver).pipe(map(arr => (arr.length > 1) ? arr[1] : null));
        }
        return userObserver;
    }

    public async getFirebaseUser(userKey: string) {
        let idToken = await this.loginService.getIdToken();

        if(!idToken) return console.error("No firebase admin user as expected");

        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': idToken
        });

        console.log("Try yo get firebase user ", userKey);
        this.http.get(MotesPaths.MOTES_REST + "/api/firebaseUsers/" + userKey, { headers: headers }).subscribe((json) => {
            console.log("Got firebase user ", json);
        });
    }

    public getSuperAdminUsers(): Promise<User[]> {

        return new Promise(async (resolve, reject) => {
            let user = await this.loginService.getCurrentUser();
            if(!user) {
                reject("No firebase user as expected");
            }

            // Get authenticated user token (must be superAdmin)
            this.http.get<{ data: any, status: string }>(MotesPaths.MOTES_REST + "/api/adminUsers", await this.getAuthOptions()).subscribe((rsp) => {
                let json = rsp.body;
                if(rsp.status == 200) {
                    let usersData = JSON.parse(json.data);
                    console.log("!!! Got admin user response ");
                    resolve(usersData.map(userData => User.fromJson(userData.key, userData)));
                }
                else {
                    reject(json.status);
                }
            });
        });
    }

    public async promoteAdminUser(userKey: string, adminRights: { isAdmin: boolean, isSuperAdmin: boolean }): Promise<boolean> {
        let user = await this.loginService.getCurrentUser();
        if(!user) return Promise.reject("No firebase user?");

        console.log("Now promote admin ", adminRights);
        return new Promise(async (resolve, reject) => {
            // Request super admin promotion of user with the given key
            let options = await this.getAuthOptions();

            this.http.patch(MotesPaths.MOTES_REST + "/api/adminUsers", { "userKey": userKey, "adminRights": adminRights }, options).subscribe((rsp) => {
                console.log("Promotes admin user rights: ", adminRights, rsp.body);
                if(rsp.status == 200) resolve(true);
                else reject();
            });
        });
    }

    public async deleteFirebaseUser(userKey: string) {
        let user = await this.loginService.getCurrentUser();
        if(!user) return Promise.reject("No firebase user?");

        console.log("Delete firebase user ", userKey);

        return new Promise(async (resolve, reject) => {
            // Request super admin promotion of user with the given key
            let options = await this.getAuthOptions();

            this.http.delete(`${MotesPaths.MOTES_REST}/api/firebaseUsers/${userKey}`, options).subscribe((rsp) => {
                (rsp.status == 200) ? resolve(true) : reject();
            });
        });
    }

    private genAdminReqMsg(user: User) {
        let userUrl = "https://console.firebase.google.com/u/1/project/motesdev/database/motesdev/data/users/private/" + user.key;
        let reqDate = new Date(user.adminRequestOn);
        let dateStr = this.datePipe.transform(reqDate, 'E dd/MM-yyyy (HH:mm:ss)'); // Sun 01-02-2019 (22:21:33)

        // Req body
        let body = {
            "mailBody": `
                Hey there Dear Motes Admin, <br><br>
                <i>${user.userName}</i> just created a new admin reuest on MotesPro. <br>
                Now you have to make a great decision.. reject or accept this request? <br><br>
                Go here and check it out: <a href='https://pro.motes.dk/users'> https://pro.motes.dk/users</a> <br><br>
                <table>
                <tr> <td> Username:     </td> <td> <i>${user.userName}</i><br>                             </td></tr>
                <tr> <td> E-mail:       </td> <td> <a href='mailto:${user.email}'><i>${user.email}</i></a> </td></tr>
                <tr> <td> User key:     </td> <td> <a href='${userUrl}'> <i>${user.key}</i></a>            </td></tr>
                <tr> <td> Request time: </td> <td> <i>${dateStr}</i>                                       </td></tr>
                </table>
                <br><br>
                Best regards, <br><br> Yours favorite <br> John the Motes Mailer Robot`,
            "userKey": user.key,
            "userName": user.userName
        };
        return body;
    }

    private async sendAdminReqMail(user: User): Promise<boolean> {
        let admin = await this.loginService.getCurrentUser();
        if(!admin) return Promise.reject("No firebase user?");

        return new Promise(async (resolve, reject) => {
            // Request super admin promotion of user with the given key
            let options = await this.getAuthOptions();
            let body = this.genAdminReqMsg(user);

            this.http.post(MotesPaths.MOTES_REST + "/api/sendAdminEmail", body, options).subscribe((rsp) => {
                if(rsp.status == 200) resolve(true);
                else reject();
            });
        });
    }

    private genAdminReqSlackMsg(user: User) {
        // Req body
        let body = {
            "mailBody": `
                Hey there Dear Motes Admin, \n\n
                '${user.userName}' just created a new admin request on StoryHunt Studio. \n
                Now you have to make a great decision.. reject or accept this request? \n\n
                Go here and check it out: https://studio.storyhunt.dk/users`,
            "userKey": user.key,
            "userName": user.userName
        };
        return body;
    }

    private async sendAdminReqSlack(user: User) {
        let admin = await this.loginService.getCurrentUser();
        if(!admin) return Promise.reject("No firebase user?");

        return new Promise(async (resolve, reject) => {
            // Request super admin promotion of user with the given key
            let options = await this.getAuthOptions();
            let body = this.genAdminReqSlackMsg(user);

            this.http.post(MotesPaths.MOTES_REST + "/api/sendAdminSlack", body, options).subscribe((rsp) => {
                if(rsp.status == 200) resolve(true);
                else reject();
            });
        });
    }

    public async getCurrentUserInfo(): Promise<{ userRights: UserRights, idToken: string, user: User }> {
        let user = await this.loginService.getCurrentUser();
        if(!user) return Promise.reject("No firebase user?");

        return new Promise(async (resolve, reject) => {

            let userRights = await this.loginService.getUserCurrentRights();
            if(!userRights) return reject(null);

            let idToken = await this.loginService.getIdToken();
            let user = await this.getUser(userRights.uid);
            resolve({ userRights: userRights, idToken: idToken, user: user });
        });
    }

    public updateAdminRights(userKey: string, isAdmin: boolean, isSuperAdmin: boolean): Promise<boolean> {
        let adminRights = {
            "isAdmin": isAdmin,
            "isSuperAdmin": isSuperAdmin
        };
        return this.promoteAdminUser(userKey, adminRights);
    }

    private async getAuthOptions(): Promise<{ headers: HttpHeaders, observe: 'response' }> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': [await this.loginService.getIdToken()]
        });
        return { headers: headers, observe: 'response' };
    }

    public async getShallowUserList(): Promise<string[]> {
        const firebaseUser = await this.loginService.getCurrentUser();
        const token = await firebaseUser.getIdToken();
        // Get object with all shallow sessions
        const json = firstValueFrom(this.http.get(FIREBASE_REST + "/users/public.json?shallow=true&auth=" + token));
        // Get session key array
        let keys = Object.keys(json).sort();
        return keys;
    }

    public watchUsersPagination(max: number = 10000, start: number): Observable<any[]> {
        // Observable list with changes of users with a email address (private part)
        let privateObs = this.af.list(usersPrivateRef, query => query.orderByChild('createdOn').startAt(start).limitToFirst(max));
        // Switch map users in private list (public part)
        return privateObs.snapshotChanges().pipe(debounceTime(2000), tap(pri => console.log("Got private ", pri.length)), switchMap(privateList => this.watchUsers(this.snapKeys(privateList)),
            // Combine public and private part with switchMap selector function
            (privateList, publicList) => publicList.map((user: User) => {
                // Map private snap with public user object
                let privateSnap = privateList.find(snap => (snap.key == user.key));
                return user.updateFromJson({ private: privateSnap ? privateSnap.payload.val() : {} });
            })));
    }

    public saveActiveProject(userKey: string, activeProjectKey: string, activeRouteKey: string = "", activePreviewKey: string = "") {
        if(!activeProjectKey) {
            // Just ignore empty project save attempts
            //console.error("Trying to save a empty active project ", activeProjectKey);
            return;
        }

        if(!userKey) {
            console.log("No user to save active project in ", userKey);
            return;
        }

        this.af.object(usersPrivateRef + userKey).update({ "activeProject": activeProjectKey, "activeRoute": activeRouteKey, "activePreview": activePreviewKey });
    }

    public saveVoteState(moteKey: string, userKey: string, isLovedByUser: boolean): boolean {
        // If no user logged in, nothing todo
        if(!userKey || !moteKey) {
            console.error("No user/mote key, so no vote can be made");
            return isLovedByUser;
        }

        // Duplicate user vote in motes (this is NoSQL, yes)
        // We could make it a little safer with a transaction,
        // but wtf its not that likely to fail and not a very critical transaction
        let moteRef = this.af.object("motes/" + moteKey + "/votes/" + userKey);
        let userRef = this.af.object(usersPublicRef + userKey + "/votes/" + moteKey);

        if(!isLovedByUser) {
            // Delete users vote
            moteRef.remove();
            userRef.remove();
            return false;
        }
        let vote = new Vote({
            createdBy: userKey,
            moteKey: moteKey,
            createdOn: this.af.serverTime()
        });

        // Create vote
        moteRef.update(vote.toJson());
        userRef.update(vote.toJson());
        return true;
    }

    /**
    * Set all votes of user at once (for migration)
    */
    public setUserVotes(userKey: string, votes: { [moteKey: string]: Vote }) {
        // If no user logged in, nothing todo
        if(!userKey) return console.error("No user key, so no vote can be made");
        let userRef = this.af.object(usersPublicRef + userKey + "/votes");
        userRef.set(votes);
    }

    public getUser(uid: string): Promise<User> {
        return this.watchUser(uid, true).pipe(take(1)).toPromise();
    }

    public getServerTimestamp(): any {
        return this.af.serverTime();
    }

    public async saveDeviceToken(deviceToken: string) {
        const user = await firstValueFrom(this.watchCurrentUser());
        const deviceKey = this.statsService.getDeviceKey();
        const deviceTokenInfo = {
            deviceKey,
            token: deviceToken,
            lastUpdatedOn: new Date(),
        };

        user.deviceTokenInfo[deviceKey] = deviceTokenInfo;
        await this.updateUser(user);
    }
}
